import { Box, IconButton, Paper, Tooltip, Typography, TextField, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/SaveAs';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { removeGlobalNote, updateOrderCommentsInRedux } from '../../../../redux/slices/orders';
import { useDispatch, useSelector } from '../../../../redux/store';
import Iconify from '../../../../reusable-components/iconify';
import { ScopeGuard } from '../../../../reusable-components/scopes';
import { addHyperlink } from '../../../../utils/formatText';
import { objectToFormData } from '../../../../api/utils';
import axiosInstance from '../../../../utils/axios';

VendorNote.propTypes = {
  item: PropTypes.object,
};

export default function VendorNote({ item }) {
  const dispatch = useDispatch();
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const filter = searchParams.get('filter');

  const [openNote, setOpenNote] = useState(null);
  const [openNoteId, setOpenNoteId] = useState(null);
  const [currentNote, setCurrentNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { orderById } = useSelector((state) => state.orders || {});
  const { orderComments, vendors, globalNote, removeGlobalNoteFromVendors } = orderById[id] || {};

  const updateNotesInRedux = (newNotes) => dispatch(updateOrderCommentsInRedux({
    orderId: id,
    commentArray: newNotes
  }));

  const noteForm = (vendorId) => {
    const data = { orderNumber: id, comment: currentNote, vendorIds: [vendorId] };
    if (openNoteId) data.id = openNoteId;
    setLoading(true);
    axiosInstance.post('/OrderCommentForm', objectToFormData(data))
      .then((res) => {
        if (res.data) {
          updateNotesInRedux(openNoteId ? orderComments.map((note) => (note.id === openNoteId ? res.data : note)) : [...orderComments, res.data]);
          enqueueSnackbar(`Note ${(openNoteId ? "updated" : "added")}`, { variant: "success" })
        } else enqueueSnackbar(`Failed to ${(openNoteId ? "update" : "add")} note`, { variant: "error" });
      })
      .catch(() => enqueueSnackbar(`Failed to ${(openNoteId ? "update" : "add")} note`, { variant: "error" }))
      .finally(() => {
        clearNoteInput();
        setLoading(false);
      });
  };  

  const deleteNote = (noteId) => {
    setDeleting(true);
    axiosInstance.post(`/deleteOrderComment?id=${noteId}`)
      .then(() => {
        updateNotesInRedux(orderComments.filter((note) => note.id !== noteId));
        enqueueSnackbar(`Note deleted`, { variant: "success" })
      })
      .catch(() => enqueueSnackbar(`Failed to delete note`, { variant: "success" }))
      .finally(() => setDeleting(false));   
  };

  const handleEdit = (comment, noteId) => {
    setOpenNote(true);
    setOpenNoteId(noteId);
    setCurrentNote(comment);
  };

  const clearNoteInput = () => {
    setCurrentNote('');
    setOpenNote(false);
    setOpenNoteId(null);
  };

  const filterUniqueNotesByVendorId = (notes) => {
    const uniqueNotes = {};
    notes.forEach((note) => {
      const vendorId = note.vendorIds?.[0];
      if (vendorId) {
        if (!uniqueNotes[vendorId]) uniqueNotes[vendorId] = [];
        uniqueNotes[vendorId].push(note);
      }
    });
    return uniqueNotes;
  };

  const uniqueNotesLookup = useMemo(() => filterUniqueNotesByVendorId(orderComments || []), [orderComments]);
  const vendorAllowsNotes = vendors?.find((vendor) => vendor.id === item.vendorId)?.allowsMessages;

  return (
    <Box sx={{ mt: 1, ml: 2 }}>
      <Box display="flex" sx={{ pl: 1 }}>
        <Tooltip title={!vendorAllowsNotes && "Vendor doesn't accept notes."} placement="top" arrow>
          <>
            <Typography component="span" sx={{ color: (theme) => (vendorAllowsNotes ? theme.palette.secondary.main : theme.palette.grey[500]) }}>
              <Iconify icon="material-symbols:add-notes-outline-rounded" width={16} height={16} sx={{ mr: 0.5 }} />
            </Typography>
            {vendorAllowsNotes && !uniqueNotesLookup[item.vendorId] ? <ScopeGuard scopes={['Edit-Order']} allowAdmin>
              <Tooltip title="Click to add a note" placement="top" arrow>
                <Typography onClick={() => setOpenNote(openNote => !openNote)} variant="subtitle2" component="span" sx={{ textDecoration: 'underline' }} >
                  Vendor notes:
                </Typography>
              </Tooltip>
            </ScopeGuard> : <Typography variant="subtitle2" component="span" sx={{ color: (theme) => !vendorAllowsNotes && theme.palette.grey[500], textDecoration: 'underline' }}>
              Vendor notes:
            </Typography>}
          </>
        </Tooltip>
      </Box>

      {vendorAllowsNotes && (
        <Box sx={{ pl: 2 }}>
          {globalNote && !removeGlobalNoteFromVendors?.includes(item.vendorId) && filter !== 'rerun' && (
            <Box sx={{ display: 'flex' }}>
              <Typography variant="subtitle2" component="span">
                <IconButton color="error" onClick={() => dispatch(removeGlobalNote(id, item.vendorId))} sx={{ m: 0, p: 0, ':hover': { color: "error.dark" } }} disableRipple>
                  <Iconify icon="material-symbols:delete" width={16} height={16} />
                </IconButton>             
                <Box component="span" sx={{ textDecoration: 'underline', mx: .5 }}>
                  Global note:
                </Box>
                {globalNote}
              </Typography>
            </Box>
          )}
          {uniqueNotesLookup[item.vendorId]?.map((note, index) => (
            <Box sx={{ display: 'flex' }} key={index}>
              <ScopeGuard scopes={['Edit-Order']} allowAdmin>
                <IconButton disabled={deleting} color="error" onClick={() => deleteNote(note.id)} sx={{ m: 0, p: 0, ':hover': { color: "error.dark" } }} disableRipple>
                  <Iconify icon="material-symbols:delete" width={16} height={16} />
                </IconButton>
                <IconButton color="info" onClick={() => handleEdit(note.comment, note.id)} sx={{ m: 0, ml: .5, mr: 1, p: 0, ':hover': { color: "info.dark" } }} disableRipple>
                  <Iconify icon="material-symbols:edit" width={16} height={16} />
                </IconButton>
              </ScopeGuard>
              <Typography variant="subtitle2">
                {addHyperlink(note.comment)}
              </Typography>
            </Box>
          ))}
          {uniqueNotesLookup[item.vendorId] && <ScopeGuard scopes={['Edit-Order']} allowAdmin>
            <Box onClick={openNote ? clearNoteInput : () => setOpenNote(true)} sx={{ display: 'flex', alignItems: 'flex-bottom', justifyContent: openNote ? "end" : "start", '&:hover': { cursor: 'pointer' } }}>
              <Typography variant="subtitle2" color="secondary" component="span" sx={{ textDecoration: 'underline', ':hover': { color: "secondary.dark" } }}>
                {openNote ? "Cancel" : "Add new note"}
              </Typography>
            </Box>
          </ScopeGuard>}
        </Box>
      )}
      {openNote && <Paper component="form" sx={{ mt: .5, ml: 1 }}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={`${openNoteId ? "Update" : "Add"} vendor note`}
          multiline
          value={currentNote}
          onChange={(e) => setCurrentNote(e.target.value)}
          autoComplete="off"
          InputProps={{
            endAdornment: (loading ? <CircularProgress size={20} /> :
              <IconButton
                color="secondary"
                onClick={() => noteForm(item.vendorId)}
                edge="end"
                disabled={!currentNote}
                sx={{ alignSelf: 'flex-end', m: 0, p: 0, ':hover': { color: "secondary.darker" } }}
                disableRipple
              >
                {openNoteId ? <SaveIcon width={18} height={18} /> : <Iconify icon="material-symbols:note-add-outline" width={20} height={20} />}
              </IconButton>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#ccc' },
              '&:hover fieldset': { borderColor: '#999' },
              '&.Mui-focused fieldset': { borderColor: '#999' },
              '&.Mui-focused': { boxShadow: 'none' },
            },
          }}
        />
      </Paper>}
    </Box>
  );
}
